/**
 * Copyright Highway9 Networks Inc.
 *
 * Client for the AI assistant chat surface exposed by saas-api at
 * /api/v1/saas/ai-assistant (REST proxy) and /api/v1/saas/ai-assistant/ws
 * (websocket bridge that streams upstream SSE events to the browser).
 *
 * Authentication: REST calls use the Bearer token like every other
 * saas-ui call; the websocket appends the JWT as a `?token=` query
 * parameter because browsers cannot set custom headers on WS handshakes.
 */

import { authenticationService } from "./authentication-service";
import type { User } from "~/types/user";

const REST_BASE = "/api/v1/saas/ai-assistant";

/** Postgres-backed session visibility: only the signed-in user, or the whole org scope. */
export type AiAssistantSessionScope = "own" | "team";

function sessionScopeQuery(scope: AiAssistantSessionScope): string {
  return `scope=${encodeURIComponent(scope)}`;
}

/** Turn upstream assistant / bridge errors into a short UI string. */
export function formatAssistantErrorMessage(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "Unknown error";

  if (
    /dial tcp/i.test(trimmed) ||
    /i\/o timeout/i.test(trimmed) ||
    /connection refused/i.test(trimmed) ||
    /connection reset/i.test(trimmed) ||
    /no such host/i.test(trimmed) ||
    /temporarily unreachable/i.test(trimmed) ||
    /opencode/i.test(trimmed) ||
    /h9-saas-opencode/i.test(trimmed) ||
    /:\s*4096\b/.test(trimmed)
  ) {
    return (
      "AI assistant is temporarily unreachable. " +
      "Live updates will resume automatically. Please try again in a moment."
    );
  }

  const prefixMatch = trimmed.match(/^(send failed|upstream stream failed|abort failed|delete failed):\s*/i);
  const prefix = prefixMatch ? `${prefixMatch[1]}: ` : "";
  const payload = prefixMatch ? trimmed.slice(prefixMatch[0].length).trim() : trimmed;

  if (payload.startsWith("{")) {
    try {
      const parsed = JSON.parse(payload) as {
        name?: string;
        data?: { message?: string; ref?: string };
        message?: string;
      };
      const inner =
        parsed?.data?.message ??
        parsed?.message ??
        (parsed?.name && parsed.name !== "UnknownError" ? parsed.name : undefined);
      if (inner) {
        const ref = parsed?.data?.ref ? ` (${parsed.data.ref})` : "";
        return `${prefix}${inner}${ref}`;
      }
    } catch {
      /* fall through */
    }
  }

  if (/opencode|h9-saas-opencode|:\s*4096\b/i.test(trimmed)) {
    return "AI assistant is temporarily unavailable. Please try again in a moment.";
  }

  return trimmed;
}

export type AiAssistantObjectRef = {
  id: string;
  name?: string;
};

export type AiAssistantSession = {
  id: string;
  title?: string;
  parentId?: string;
  ownerEmail?: string;
  user?: AiAssistantObjectRef;
  tenant?: AiAssistantObjectRef;
  isHidden?: boolean;
  createdAt?: number;
  updatedAt?: number;
  [key: string]: unknown;
};

/** OpenCode default titles before auto-title runs, e.g. "New session - 2026-05-27T12:34:56.789Z". */
export function isDefaultAssistantSessionTitle(title: string | undefined): boolean {
  const t = title?.trim();
  if (!t) return true;
  return /^(New session - |Child session - )\d{4}-\d{2}-\d{2}T/.test(t);
}

/** Normalize upstream session shapes (`info`, `session`, flat) into AiAssistantSession. */
export function normalizeAiAssistantSession(
  raw: unknown,
  fallbackId?: string
): AiAssistantSession | null {
  if (!raw || typeof raw !== "object") {
    const id = fallbackId?.trim();
    return id ? { id } : null;
  }
  const o = raw as Record<string, unknown>;
  const inner =
    o.info && typeof o.info === "object" && !Array.isArray(o.info)
      ? (o.info as Record<string, unknown>)
      : o.session && typeof o.session === "object" && !Array.isArray(o.session)
        ? (o.session as Record<string, unknown>)
        : o;

  const id = String(inner.id ?? o.id ?? fallbackId ?? "").trim();
  if (!id) return null;

  const titleRaw = inner.title ?? inner.name ?? o.title ?? o.name;
  const title =
    typeof titleRaw === "string" && titleRaw.trim() ? titleRaw.trim() : undefined;

  const parentRaw =
    inner.parentID ?? inner.parentId ?? inner.parent_id ?? o.parentId ?? o.parentID;
  const parentId =
    typeof parentRaw === "string" && parentRaw.trim() ? parentRaw.trim() : undefined;

  const user = normalizeObjectRef(inner.user ?? o.user);
  const tenant = normalizeObjectRef(inner.tenant ?? o.tenant);
  const isHidden =
    typeof inner.isHidden === "boolean"
      ? inner.isHidden
      : typeof o.isHidden === "boolean"
        ? o.isHidden
        : undefined;

  return {
    ...o,
    ...inner,
    id,
    ...(title ? { title } : {}),
    ...(parentId ? { parentId } : {}),
    ...(user ? { user } : {}),
    ...(tenant ? { tenant } : {}),
    ...(isHidden !== undefined ? { isHidden } : {}),
  };
}

function normalizeAiAssistantMessage(raw: AiAssistantMessage): AiAssistantMessage {
  const user = normalizeObjectRef(raw.user);
  return user ? { ...raw, user } : raw;
}

/** Logged-in user as an assistant object reference (for message attribution in the UI). */
export function currentAssistantUserRef(): AiAssistantObjectRef | undefined {
  const info = authenticationService.currentUserInfo as User | null;
  if (info?.id) {
    const name = info.name?.trim() || info.email?.trim();
    return name ? { id: info.id, name } : { id: info.id };
  }
  const legacy = authenticationService.currentUserValue as { id?: string; email?: string; name?: string } | null;
  if (legacy?.id) {
    const name = legacy.name?.trim() || legacy.email?.trim();
    return name ? { id: legacy.id, name } : { id: legacy.id };
  }
  return undefined;
}

/** Resolve display label for a user-authored message. */
export function userMessageAuthorLabel(
  message: AiAssistantMessage,
  sessionOwner?: AiAssistantObjectRef
): string | null {
  const fromMsg = message.user?.name?.trim() || message.user?.id;
  if (fromMsg) return fromMsg;
  const fromSession = sessionOwner?.name?.trim() || sessionOwner?.id;
  if (fromSession) return fromSession;
  const self = currentAssistantUserRef();
  return self?.name?.trim() || self?.id || null;
}

function normalizeObjectRef(raw: unknown): AiAssistantObjectRef | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const o = raw as Record<string, unknown>;
  const id = String(o.id ?? "").trim();
  if (!id) return undefined;
  const name = typeof o.name === "string" && o.name.trim() ? o.name.trim() : undefined;
  return name ? { id, name } : { id };
}

export type AiAssistantMessageInfo = {
  id?: string;
  role?: "user" | "assistant" | "system";
  sessionID?: string;
  time?: { created?: number; completed?: number };
  [key: string]: unknown;
};

export type AiAssistantMessagePart =
  | { type: "text"; text: string; [key: string]: unknown }
  | { type: "tool"; tool?: string; state?: unknown; [key: string]: unknown }
  | { type: "step-start" | "step-finish" | "reasoning" | "snapshot"; [key: string]: unknown }
  | { type: string; [key: string]: unknown };

export type AiAssistantMessage = {
  info: AiAssistantMessageInfo;
  parts: AiAssistantMessagePart[];
  user?: AiAssistantObjectRef;
};

export type AiAssistantQuestionOption = {
  label: string;
  description?: string;
};

export type AiAssistantQuestionInfo = {
  header: string;
  question?: string;
  options: AiAssistantQuestionOption[];
  multiple?: boolean;
  custom?: boolean;
};

export type AiAssistantQuestionRequest = {
  id: string;
  sessionID: string;
  questions: AiAssistantQuestionInfo[];
  tool?: { messageID: string; callID: string };
};

export type AiAssistantAgent = {
  name: string;
  description?: string;
  mode?: "primary" | "subagent" | "all";
  model?: string;
  builtin?: boolean;
  /** Optional link to a primary agent (upstream / proxy may expose this). */
  parent?: string;
  parentAgent?: string;
};

/** Canonical id of the default OpenCode primary agent (`agent.build`). */
export const DEFAULT_PRIMARY_AGENT_CANONICAL = "build";

/** OpenCode system agents — hidden from the Ziva agent picker and message author line. */
const HIDDEN_AGENT_IDS = new Set(["compaction", "plan", "summary", "title", "alice"]);

export function isAiAssistantAgentHiddenFromUi(agentName: string): boolean {
  const trimmed = agentName.trim().toLowerCase();
  if (!trimmed) return false;
  if (HIDDEN_AGENT_IDS.has(trimmed)) return true;
  const slash = trimmed.lastIndexOf("/");
  if (slash >= 0 && HIDDEN_AGENT_IDS.has(trimmed.slice(slash + 1))) return true;
  return false;
}

export function filterAgentsVisibleForUi(agents: AiAssistantAgent[]): AiAssistantAgent[] {
  return agents.filter((a) => !isAiAssistantAgentHiddenFromUi(a.name));
}

/** UI display labels for builtin agents (OpenCode 1.14.x has no `agent_display_names` config key). */
export const DEFAULT_AGENT_DISPLAY_NAMES: Record<string, string> = {
  build: "Ziva",
  Abby: "Abby",
  "Hudson/Itsy": "Itsy",
  "Max/Bitsy": "Bitsy",
};

/** Label for agent dropdowns; merges config overrides with {@link DEFAULT_AGENT_DISPLAY_NAMES}. */
export function agentDisplayLabel(
  agent: AiAssistantAgent,
  displayNames?: Record<string, string>
): string {
  const override = displayNames?.[agent.name] ?? DEFAULT_AGENT_DISPLAY_NAMES[agent.name];
  return override?.trim() || agent.name;
}

/** Read optional display-name overrides from assistant `GET /config` if a future OpenCode version adds them. */
export function pickAgentDisplayNamesFromAssistantConfig(body: unknown): Record<string, string> {
  if (!body || typeof body !== "object") return {};
  const root = body as Record<string, unknown>;
  const candidates = [
    root.agent_display_names,
    root.agentDisplayNames,
    root.config && typeof root.config === "object"
      ? (root.config as Record<string, unknown>).agent_display_names
      : undefined,
    root.data && typeof root.data === "object"
      ? (root.data as Record<string, unknown>).agent_display_names
      : undefined,
  ];
  for (const raw of candidates) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof k === "string" && typeof v === "string" && v.trim()) {
        out[k] = v.trim();
      }
    }
    if (Object.keys(out).length > 0) return out;
  }
  return {};
}

const authHeader = () => ({
  Authorization: `Bearer ${authenticationService?.currentUserValue?.token ?? ""}`,
});

/** Substrings in model ids to hide in Ziva (vendor junk or deprecated ids). Case-insensitive, matched on the full id. */
const AI_ASSISTANT_MODEL_DENY_SUBSTRINGS = [
  "big-pickle",
  "big-pickel",
  "gpt-5.3-chat-latest",
] as const;

/** True if this model id should not appear in the Ziva model dropdown. */
export function isAiAssistantModelIdDenylisted(id: string): boolean {
  const t = id.trim();
  if (!t) return true;
  const lower = t.toLowerCase();
  return AI_ASSISTANT_MODEL_DENY_SUBSTRINGS.some((s) => lower.includes(s));
}

/**
 * Leading `[H9]` badge in `saas/opencode/opencode.json` model `name` fields is listed in Ziva
 * (`collectModelIdsFromProvidersConfig`); stripped for display everywhere we use provider names.
 */
export function stripAssistantH9ModelDisplayPrefix(label: string): string {
  return label.trim().replace(/^\[H9\]\s*/, "").trim();
}

function assistantModelDeclarationHasH9Badge(val: unknown): boolean {
  const n = readModelEntryDisplayName(val);
  return typeof n === "string" && /^\s*\[H9\]/.test(n);
}

/** Resolve a declared model entry (`models` map or array object) from `GET /config/providers`. */
export function findModelValueInProvidersConfig(
  body: unknown,
  providerId: string,
  modelKey: string
): unknown | undefined {
  const pid = providerId.trim();
  const key = modelKey.trim();
  if (!pid || !key) return undefined;
  if (!body || typeof body !== "object") return undefined;
  const providers = (body as Record<string, unknown>).providers;
  if (!Array.isArray(providers)) return undefined;

  for (const p of providers) {
    if (!p || typeof p !== "object") continue;
    const pr = p as Record<string, unknown>;
    const prId = String(pr.id ?? pr.name ?? "").trim();
    if (prId !== pid) continue;
    const models = pr.models;
    if (models && typeof models === "object" && !Array.isArray(models)) {
      const v = (models as Record<string, unknown>)[key];
      if (v !== undefined) return v;
    }
    if (!Array.isArray(models)) continue;
    for (const m of models) {
      if (!m || typeof m !== "object") continue;
      const mo = m as Record<string, unknown>;
      const mid = String(mo.id ?? mo.modelID ?? mo.name ?? "").trim();
      if (!mid) continue;
      if (mid.includes("/")) {
        const i = mid.indexOf("/");
        const left = mid.slice(0, i).trim();
        const right = mid.slice(i + 1).trim();
        if (left === pid && right === key) return m;
      } else if (mid === key) {
        return m;
      }
    }
  }
  return undefined;
}

/**
 * Ziva models: canonical ids whose declared `name` (from OpenCode `/config/providers`) starts
 * with `[H9]` — matches conventions in `saas/opencode/opencode.json`.
 */
export function collectModelIdsFromProvidersConfig(body: unknown): string[] {
  const seen = new Set<string>();
  const add = (s: string) => {
    const t = s.trim();
    if (t && !isAiAssistantModelIdDenylisted(t)) seen.add(t);
  };
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const providers = root.providers;

  if (Array.isArray(providers)) {
    for (const p of providers) {
      if (!p || typeof p !== "object") continue;
      const pr = p as Record<string, unknown>;
      const providerId = String(pr.id ?? pr.name ?? "").trim();
      const models = pr.models;

      if (Array.isArray(models)) {
        for (const m of models) {
          if (typeof m === "string") continue;
          if (!m || typeof m !== "object") continue;
          if (!assistantModelDeclarationHasH9Badge(m)) continue;
          const mo = m as Record<string, unknown>;
          const mid = String(mo.id ?? mo.modelID ?? mo.name ?? "").trim();
          if (!mid) continue;
          if (mid.includes("/")) {
            const i = mid.indexOf("/");
            add(`${mid.slice(0, i).trim()}/${mid.slice(i + 1).trim()}`);
          } else if (providerId) {
            add(`${providerId}/${mid}`);
          } else add(mid);
        }
      } else if (models && typeof models === "object") {
        for (const [modelKey, val] of Object.entries(models)) {
          const k = modelKey.trim();
          if (!k || !providerId || !assistantModelDeclarationHasH9Badge(val)) continue;
          add(`${providerId}/${k}`);
        }
      }
    }
  }

  const defMap = root.default;
  if (defMap && typeof defMap === "object") {
    for (const [providerKey, raw] of Object.entries(defMap)) {
      if (typeof raw !== "string" || !raw.trim()) continue;
      const modelPart = raw.trim();
      const pk = providerKey.trim();
      const canonical = modelPart.includes("/") ? modelPart : pk ? `${pk}/${modelPart}` : "";
      const i = canonical.indexOf("/");
      if (i <= 0) continue;
      const left = canonical.slice(0, i).trim();
      const right = canonical.slice(i + 1).trim();
      const meta = findModelValueInProvidersConfig(body, left, right);
      if (assistantModelDeclarationHasH9Badge(meta)) add(canonical);
    }
  }

  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** One logical model under a provider: canonical id is `${providerId}/${modelKey}` (OpenCode map keys, not upstream `api.id`). */
export type ProviderModelCanonicalEntry = {
  providerId: string;
  modelKey: string;
  canonical: string;
  /** Upstream / vendor id when present (e.g. `google/gemma-4-31B`); used to rewrite agent defaults to canonical id. */
  apiId?: string;
};

function readApiIdFromModelValue(val: unknown): string | undefined {
  if (!val || typeof val !== "object") return undefined;
  const api = (val as Record<string, unknown>).api;
  if (!api || typeof api !== "object") return undefined;
  const id = (api as Record<string, unknown>).id;
  return typeof id === "string" && id.trim() ? id.trim() : undefined;
}

function readModelEntryDisplayName(val: unknown): string | undefined {
  if (!val || typeof val !== "object") return undefined;
  const n = (val as Record<string, unknown>).name;
  return typeof n === "string" && n.trim() ? n.trim() : undefined;
}

/**
 * Walks `GET /config/providers` and returns canonical `providerId/modelKey` entries,
 * including `api.id` when declared (object-shaped `models` use the map key as modelKey).
 */
export function collectProviderModelCanonicalEntries(body: unknown): ProviderModelCanonicalEntry[] {
  const out: ProviderModelCanonicalEntry[] = [];
  if (!body || typeof body !== "object") return out;
  const providers = (body as Record<string, unknown>).providers;
  if (!Array.isArray(providers)) return out;

  const push = (providerId: string, modelKey: string, apiId?: string) => {
    const pid = providerId.trim();
    const key = modelKey.trim();
    if (!pid || !key) return;
    const canonical = `${pid}/${key}`;
    const aid = apiId?.trim();
    const entry: ProviderModelCanonicalEntry = { providerId: pid, modelKey: key, canonical };
    if (aid) entry.apiId = aid;
    out.push(entry);
  };

  for (const p of providers) {
    if (!p || typeof p !== "object") continue;
    const pr = p as Record<string, unknown>;
    const providerId = String(pr.id ?? pr.name ?? "").trim();
    const models = pr.models;

    if (Array.isArray(models)) {
      for (const m of models) {
        if (typeof m === "string") {
          const mid = m.trim();
          if (!mid) continue;
          if (mid.includes("/")) {
            const i = mid.indexOf("/");
            push(mid.slice(0, i).trim(), mid.slice(i + 1).trim());
          } else if (providerId) {
            push(providerId, mid);
          }
        } else if (m && typeof m === "object") {
          const mo = m as Record<string, unknown>;
          const apiId = readApiIdFromModelValue(mo);
          const mid = String(mo.id ?? mo.modelID ?? mo.name ?? "").trim();
          if (!mid) continue;
          if (mid.includes("/")) {
            const i = mid.indexOf("/");
            push(mid.slice(0, i).trim(), mid.slice(i + 1).trim(), apiId);
          } else if (providerId) {
            push(providerId, mid, apiId);
          }
        }
      }
    } else if (models && typeof models === "object" && !Array.isArray(models)) {
      for (const [modelKey, val] of Object.entries(models)) {
        const key = modelKey.trim();
        if (!key) continue;
        const apiId = readApiIdFromModelValue(val);
        if (providerId) push(providerId, key, apiId);
      }
    }
  }
  return out;
}

/**
 * Maps model ids (`providerId/modelKey`, object `id`, and `api.id`) to human-readable
 * `name` values on provider `models` entries (OpenCode-style config).
 */
export function buildAssistantModelIdToLabelMap(body: unknown): Map<string, string> {
  const map = new Map<string, string>();
  const put = (modelRef: string, label: string | undefined) => {
    const r = modelRef.trim();
    const t = label?.trim();
    if (!r || !t) return;
    const display = stripAssistantH9ModelDisplayPrefix(t);
    if (!map.has(r)) map.set(r, display);
  };

  if (!body || typeof body !== "object") return map;
  const providers = (body as Record<string, unknown>).providers;
  if (!Array.isArray(providers)) return map;

  for (const p of providers) {
    if (!p || typeof p !== "object") continue;
    const pr = p as Record<string, unknown>;
    const providerId = String(pr.id ?? pr.name ?? "").trim();
    const models = pr.models;

    if (Array.isArray(models)) {
      for (const m of models) {
        if (typeof m === "string") continue;
        if (!m || typeof m !== "object") continue;
        const mo = m as Record<string, unknown>;
        const label = readModelEntryDisplayName(mo);
        if (!label) continue;
        const apiId = readApiIdFromModelValue(mo);
        const mid = String(mo.id ?? mo.modelID ?? mo.name ?? "").trim();
        if (!mid) continue;
        if (mid.includes("/")) {
          const i = mid.indexOf("/");
          const left = mid.slice(0, i).trim();
          const right = mid.slice(i + 1).trim();
          if (left && right) {
            put(`${left}/${right}`, label);
          }
        } else if (providerId) {
          put(`${providerId}/${mid}`, label);
        }
        if (apiId) put(apiId, label);
      }
    } else if (models && typeof models === "object" && !Array.isArray(models)) {
      for (const [modelKey, val] of Object.entries(models)) {
        const key = modelKey.trim();
        if (!key || !providerId) continue;
        const label = readModelEntryDisplayName(val);
        if (!label) continue;
        const canonical = `${providerId}/${key}`;
        put(canonical, label);
        const apiId = readApiIdFromModelValue(val);
        if (val && typeof val === "object") {
          const oid = (val as Record<string, unknown>).id;
          if (typeof oid === "string" && oid.trim()) put(oid.trim(), label);
        }
        if (apiId) put(apiId, label);
      }
    }
  }
  return map;
}

/**
 * Rewrites the model string sent on chat (WebSocket / REST) to canonical `providerId/modelKey`
 * from providers config: bare keys, wrong `api.id`, or mismatched `otherProvider/modelKey` are
 * corrected when uniquely resolvable. Falls back to the trimmed input when unknown.
 */
export function normalizeOutgoingAssistantModel(
  model: string | undefined,
  providersConfig: unknown
): string | undefined {
  const raw = typeof model === "string" ? model.trim() : "";
  if (!raw) return undefined;

  const entries = collectProviderModelCanonicalEntries(providersConfig);
  if (entries.length === 0) return raw;

  for (const e of entries) {
    if (e.canonical === raw) return e.canonical;
  }

  for (const e of entries) {
    if (e.apiId && e.apiId === raw) return e.canonical;
  }

  const byBareKey = entries.filter((e) => e.modelKey === raw);
  if (byBareKey.length === 1) return byBareKey[0].canonical;

  if (raw.includes("/")) {
    const i = raw.indexOf("/");
    const left = raw.slice(0, i).trim();
    const right = raw.slice(i + 1).trim();
    if (right) {
      const bySuffix = entries.filter((e) => e.modelKey === right);
      if (bySuffix.length === 1) return bySuffix[0].canonical;
      const byBoth = entries.filter((e) => e.providerId === left && e.modelKey === right);
      if (byBoth.length === 1) return byBoth[0].canonical;
    }
  }

  return raw;
}

/** Readable fallback when provider config has no display `name` (last path segment, hyphens → spaces). */
export function assistantModelIdFallbackLabel(canonicalId: string): string {
  const t = canonicalId.trim();
  if (!t) return t;
  const last = t.includes("/") ? t.slice(t.lastIndexOf("/") + 1) : t;
  return last.replace(/-/g, " ");
}

/**
 * Label for the model dropdown: uses `buildAssistantModelIdToLabelMap` when `labelMap` omitted,
 * else the supplied map (for memoization). Values sent on the wire stay canonical ids.
 */
export function assistantModelMenuLabel(
  rawId: string,
  providersConfig: unknown,
  labelMap?: Map<string, string>
): string {
  const id = rawId.trim();
  if (!id) return id;
  const map = labelMap ?? buildAssistantModelIdToLabelMap(providersConfig);
  const canonical = normalizeOutgoingAssistantModel(id, providersConfig) ?? id;
  return map.get(id) ?? map.get(canonical) ?? assistantModelIdFallbackLabel(canonical);
}

const safeJson = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const ensureOk = async (response: Response, op: string) => {
  if (!response.ok) {
    const body = await safeJson(response);
    const msg =
      (body && typeof body === "object" && "errors" in body
        ? // saas-style { errors: [{ message }] }
          (body as any).errors?.[0]?.message
        : undefined) ||
      (typeof body === "string" ? body : undefined) ||
      `${response.status} ${response.statusText}`;
    throw new Error(`AI assistant ${op} failed: ${msg}`);
  }
};

function coerceConfigModelField(raw: unknown): string | undefined {
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    const prov = o.providerID ?? o.provider ?? o.providerId;
    const mid = o.modelID ?? o.modelId ?? o.id;
    if (typeof prov === "string" && typeof mid === "string" && prov.trim() && mid.trim()) {
      const p = prov.trim();
      const m = mid.trim();
      return m.includes("/") ? m : `${p}/${m}`;
    }
  }
  return undefined;
}

/** Global default from assistant `GET /config` (upstream `model` field at runtime). */
export function pickTopLevelModelFromAssistantConfig(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const o = body as Record<string, unknown>;
  if ("data" in o && o.data && typeof o.data === "object") {
    const inner = pickTopLevelModelFromAssistantConfig(o.data);
    if (inner) return inner;
  }
  const fromTop = coerceConfigModelField(o.model);
  if (fromTop) return fromTop;
  const nested = o.config;
  if (nested && typeof nested === "object") {
    const fromNested = coerceConfigModelField((nested as Record<string, unknown>).model);
    if (fromNested) return fromNested;
  }
  return undefined;
}

/** Global default model id from `GET /config/providers` (`default` provider → model map). */
export function pickGlobalDefaultModelId(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const defMap = (body as Record<string, unknown>).default;
  if (!defMap || typeof defMap !== "object") return undefined;
  for (const [providerKey, raw] of Object.entries(defMap)) {
    if (typeof raw !== "string" || !raw.trim()) continue;
    const modelPart = raw.trim();
    if (modelPart.includes("/")) return modelPart;
    const pk = providerKey.trim();
    if (pk) return `${pk}/${modelPart}`;
  }
  return undefined;
}

/**
 * Canonical model id used when Ziva's picker is on "Default": selected agent's
 * model, else the primary (`build`) agent, else GET /config `model`, else the
 * providers default map.
 */
export function resolveZivaDefaultEffectiveModelId(args: {
  agents: AiAssistantAgent[];
  selectedAgent?: string;
  assistantServerConfig: unknown;
  providersConfig: unknown;
}): string | null {
  const canon = (raw: string) =>
    (normalizeOutgoingAssistantModel(raw.trim(), args.providersConfig) ?? raw.trim()).trim();
  const picked = args.selectedAgent
    ? args.agents.find((a) => a.name === args.selectedAgent)
    : undefined;
  if (picked?.model?.trim()) return canon(picked.model);
  const primary =
    args.agents.find((a) => a.mode === "primary" && a.name === DEFAULT_PRIMARY_AGENT_CANONICAL) ??
    args.agents.find((a) => a.mode === "primary") ??
    args.agents[0];
  if (primary?.model?.trim()) return canon(primary.model);
  const fromServerConfig = pickTopLevelModelFromAssistantConfig(args.assistantServerConfig);
  if (fromServerConfig?.trim()) return canon(fromServerConfig);
  const fromProviders = pickGlobalDefaultModelId(args.providersConfig);
  if (fromProviders?.trim()) return canon(fromProviders);
  return null;
}

export const aiAssistantRest = {
  async health(): Promise<{ healthy: boolean; version?: string }> {
    const r = await fetch(`${REST_BASE}/health`, { headers: { ...authHeader() } });
    await ensureOk(r, "health");
    return (await safeJson(r)) as any;
  },

  async listAgents(): Promise<AiAssistantAgent[]> {
    const r = await fetch(`${REST_BASE}/agents`, { headers: { ...authHeader() } });
    await ensureOk(r, "list agents");
    const body = (await safeJson(r)) as { data?: AiAssistantAgent[] } | null;
    return body?.data ?? [];
  },

  /** Upstream `GET /config` — includes top-level `model` (server default). */
  async fetchConfig(): Promise<unknown> {
    const r = await fetch(`${REST_BASE}/config`, { headers: { ...authHeader() } });
    await ensureOk(r, "get assistant config");
    return await safeJson(r);
  },

  /** Upstream `GET /config/providers` — shape varies slightly by upstream version. */
  async listConfigProviders(): Promise<unknown> {
    const r = await fetch(`${REST_BASE}/config/providers`, { headers: { ...authHeader() } });
    await ensureOk(r, "list config providers");
    return await safeJson(r);
  },

  async listSessions(scope: AiAssistantSessionScope = "team"): Promise<AiAssistantSession[]> {
    const r = await fetch(`${REST_BASE}/sessions?${sessionScopeQuery(scope)}`, {
      headers: { ...authHeader() },
    });
    await ensureOk(r, "list sessions");
    const body = (await safeJson(r)) as { data?: AiAssistantSession[] } | null;
    return (body?.data ?? [])
      .map((s) => normalizeAiAssistantSession(s))
      .filter((s): s is AiAssistantSession => Boolean(s?.id));
  },

  async getSession(id: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      headers: { ...authHeader() },
    });
    await ensureOk(r, "get session");
    const body = await safeJson(r);
    let raw: unknown = body;
    if (body && typeof body === "object" && "data" in (body as object) && (body as any).data) {
      raw = (body as { data: unknown }).data;
    }
    return normalizeAiAssistantSession(raw, id) ?? { id };
  },

  /** Load a session-scoped Highcharts JSON config saved by chart-tool. */
  async fetchSessionChart(sessionId: string, filename: string): Promise<Record<string, unknown>> {
    const safeName = filename.trim();
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(sessionId)}/charts/${encodeURIComponent(safeName)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "load chart");
    const body = await safeJson(r);
    if (!body || typeof body !== "object" || Array.isArray(body)) {
      throw new Error("Invalid chart configuration");
    }
    return body as Record<string, unknown>;
  },

  /** Load a session-scoped HTML report saved by report-tool. */
  async fetchSessionReport(sessionId: string, filename: string): Promise<string> {
    const safeName = filename.trim();
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(sessionId)}/reports/${encodeURIComponent(safeName)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "load report");
    return r.text();
  },

  async createSession(title?: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(title ? { title } : {}),
    });
    await ensureOk(r, "create session");
    const body = await safeJson(r);
    return normalizeAiAssistantSession(body) ?? { id: "" };
  },

  async deleteSession(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "delete session");
  },

  /** Updates display title in Postgres only (not sent to the upstream assistant). */
  async patchSessionTitle(id: string, title: string): Promise<AiAssistantSession> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ title }),
    });
    await ensureOk(r, "update session title");
    const body = await safeJson(r);
    const existing = normalizeAiAssistantSession(body, id);
    return existing ?? { id, title };
  },

  async listMessages(
    id: string,
    limit = 50,
    scope: AiAssistantSessionScope = "team"
  ): Promise<AiAssistantMessage[]> {
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(id)}/messages?limit=${limit}&${sessionScopeQuery(scope)}`,
      { headers: { ...authHeader() } }
    );
    await ensureOk(r, "list messages");
    const body = (await safeJson(r)) as { data?: AiAssistantMessage[] } | null;
    return (body?.data ?? []).map((m) => normalizeAiAssistantMessage(m));
  },

  async sendMessage(
    id: string,
    body: { prompt: string; agent?: string; model?: string }
  ): Promise<AiAssistantMessage> {
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(id)}/messages`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(body),
      }
    );
    await ensureOk(r, "send message");
    return (await safeJson(r)) as AiAssistantMessage;
  },

  async sendMessageAsync(
    id: string,
    body: { prompt: string; agent?: string; model?: string }
  ): Promise<void> {
    const r = await fetch(
      `${REST_BASE}/sessions/${encodeURIComponent(id)}/messages/async`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(body),
      }
    );
    await ensureOk(r, "send message async");
  },

  async abort(id: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/sessions/${encodeURIComponent(id)}/abort`, {
      method: "POST",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "abort");
  },

  async listPendingQuestions(): Promise<AiAssistantQuestionRequest[]> {
    const r = await fetch(`${REST_BASE}/questions`, { headers: { ...authHeader() } });
    await ensureOk(r, "list pending questions");
    const body = (await safeJson(r)) as { data?: AiAssistantQuestionRequest[] } | AiAssistantQuestionRequest[] | null;
    if (Array.isArray(body)) return body;
    return body?.data ?? [];
  },

  async replyToQuestion(requestID: string, answers: string[][]): Promise<void> {
    const r = await fetch(`${REST_BASE}/questions/${encodeURIComponent(requestID)}/reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ answers }),
    });
    await ensureOk(r, "reply to question");
  },

  async rejectQuestion(requestID: string): Promise<void> {
    const r = await fetch(`${REST_BASE}/questions/${encodeURIComponent(requestID)}/reject`, {
      method: "POST",
      headers: { ...authHeader() },
    });
    await ensureOk(r, "reject question");
  },
};

// ---------------------------------------------------------------------------
// WebSocket bridge
// ---------------------------------------------------------------------------

export type ServerEnvelope =
  | { type: "server.connected"; email: string }
  | { type: "server.event"; sessionID: string; event: any }
  | { type: "server.message_complete"; sessionID: string; message: AiAssistantMessage }
  | { type: "server.session_created"; session: AiAssistantSession }
  | { type: "server.session_deleted"; sessionID: string }
  | { type: "server.aborted"; sessionID: string }
  | { type: "server.error"; message: string }
  | { type: "server.pong"; ts: number };

export type ClientEnvelope =
  | { type: "client.ping" }
  | { type: "client.subscribe"; data: { sessionIDs: string[] } }
  | { type: "client.create_session"; data: { title?: string } }
  | {
    type: "client.send_message";
    data: { sessionID: string; body: { prompt: string; agent?: string; model?: string; timezone?: string } };
  }
  | { type: "client.abort"; data: { sessionID: string } }
  | { type: "client.delete_session"; data: { sessionID: string } };

export interface AiAssistantSocketHandlers {
  onConnected?: (email: string) => void;
  onEvent?: (sessionID: string, event: any) => void;
  onSessionCreated?: (session: AiAssistantSession) => void;
  onSessionDeleted?: (sessionID: string) => void;
  onAborted?: (sessionID: string) => void;
  onMessageComplete?: (sessionID: string, message: AiAssistantMessage) => void;
  onError?: (message: string) => void;
  onClose?: () => void;
}

export class AiAssistantSocket {
  private ws?: WebSocket;
  private retry = 0;
  private closedByClient = false;
  private subscriptions: Set<string> = new Set();
  private pingTimer?: ReturnType<typeof setInterval>;
  private reconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(private handlers: AiAssistantSocketHandlers) {}

  connect() {
    this.closedByClient = false;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const token = authenticationService?.currentUserValue?.token ?? "";
    const url = `${proto}://${window.location.host}/api/v1/saas/ai-assistant/ws?token=${encodeURIComponent(token)}`;
    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.retry = 0;
      // Re-subscribe after a reconnect
      if (this.subscriptions.size > 0) {
        this.send({ type: "client.subscribe", data: { sessionIDs: [...this.subscriptions] } });
      }
      this.pingTimer = setInterval(() => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({ type: "client.ping" });
        }
      }, 25_000);
    };

    this.ws.onmessage = (ev) => {
      let msg: ServerEnvelope;
      try {
        msg = JSON.parse(ev.data) as ServerEnvelope;
      } catch {
        return;
      }
      this.dispatch(msg);
    };

    this.ws.onclose = () => {
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.handlers.onClose?.();
      if (!this.closedByClient) {
        const wait = Math.min(15_000, 1000 * 2 ** this.retry);
        this.retry += 1;
        this.reconnectTimer = setTimeout(() => this.connect(), wait);
      }
    };

    this.ws.onerror = () => {
      // onclose will follow
    };
  }

  close() {
    this.closedByClient = true;
    if (this.pingTimer) clearInterval(this.pingTimer);
    // Cancel any pending reconnect so close() is final (MCS-4316)
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
    const ws = this.ws;
    this.ws = undefined;
    if (ws) {
      // Detach before closing. Frames already buffered still fire onmessage during the close
      // handshake, and onopen can still land if we were mid-connect, installing a ping interval
      // that nothing clears. onclose stays bound so the consumer still sees the disconnect.
      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.close();
    }
  }

  send(envelope: ClientEnvelope) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(envelope));
      return true;
    }
    return false;
  }

  subscribe(sessionIDs: string[]) {
    this.subscriptions = new Set(sessionIDs);
    this.send({ type: "client.subscribe", data: { sessionIDs } });
  }

  addSubscription(sessionID: string) {
    if (!sessionID || this.subscriptions.has(sessionID)) return;
    this.subscriptions.add(sessionID);
    this.send({
      type: "client.subscribe",
      data: { sessionIDs: [...this.subscriptions] },
    });
  }

  private dispatch(raw: unknown) {
    if (!raw || typeof raw !== "object") return;
    const m = raw as Record<string, unknown>;
    const type = String(m.type ?? "").toLowerCase();

    switch (type) {
      case "server.connected":
        this.handlers.onConnected?.(String(m.email ?? ""));
        break;

      case "server.event": {
        const sid = String(m.sessionID ?? m.sessionId ?? "");
        const ev = m.event !== undefined ? m.event : m;
        this.handlers.onEvent?.(sid, ev);
        break;
      }

      case "server.message_complete": {
        const sessionID = String(m.sessionID ?? m.sessionId ?? "");
        let message = m.message as any;
        if (message && typeof message === "object" && !message.info) {
          if (message.message?.info) {
            message = message.message;
          }
        }
        if (sessionID && message && typeof message === "object" && message.info?.id) {
          this.handlers.onMessageComplete?.(sessionID, message as AiAssistantMessage);
        }
        break;
      }

      case "server.session_created":
        this.handlers.onSessionCreated?.(m.session as AiAssistantSession);
        break;

      case "server.session_deleted":
        this.handlers.onSessionDeleted?.(String(m.sessionID ?? m.sessionId ?? ""));
        break;

      case "server.aborted":
        this.handlers.onAborted?.(String(m.sessionID ?? m.sessionId ?? ""));
        break;

      case "server.error":
        this.handlers.onError?.(
          formatAssistantErrorMessage(
            String(m.message ?? (m as any).error ?? "unknown error")
          )
        );
        break;

      case "server.pong":
        break;

      default:
        break;
    }
  }
}
